import React, { useEffect, useState } from 'react';
import {
  CreditCard, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Plug, ShieldCheck, Copy,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

/**
 * Stripe configuration — platform owner only.
 *
 * Secrets are write-only: they can be entered and replaced, but never read back. The
 * fields show a masked hint of what is stored so the operator can tell whether a key is
 * set, without the page ever holding a live secret key it could leak.
 */

const WEBHOOK_URL = 'https://app.rostery.com.au/api/billing/webhook';

export default function BillingTab() {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState({ mode: 'test', secretKey: '', publishableKey: '', webhookSecret: '' });
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/billing-admin/settings');
      setStatus(data);
      setForm(f => ({ ...f, mode: data.mode || 'test' }));
      api.get('/billing-admin/events').then(r => setEvents(r.data || [])).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load billing settings');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy('save');
    try {
      // Blank fields are omitted so one key can be rotated without re-entering the others.
      const body = { mode: form.mode };
      for (const k of ['secretKey', 'publishableKey', 'webhookSecret']) {
        if (form[k].trim()) body[k] = form[k].trim();
      }
      const { data } = await api.put('/billing-admin/settings', body);
      setStatus(data);
      setForm({ mode: data.mode, secretKey: '', publishableKey: '', webhookSecret: '' });
      toast.success('Stripe settings saved');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save');
    } finally { setBusy(''); }
  };

  const test = async () => {
    setBusy('test'); setTestResult(null);
    try {
      const { data } = await api.post('/billing-admin/test');
      setTestResult(data);
      toast.success('Stripe connection is working');
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.message || 'Failed' });
      toast.error('Stripe rejected the key');
    } finally { setBusy(''); }
  };

  const sync = async () => {
    setBusy('sync');
    try {
      const { data } = await api.post('/billing-admin/sync-catalogue');
      const created = data.results.filter(r => r.status === 'created').length;
      toast.success(`Catalogue synced — ${created} created, ${data.synced - created} already present`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Sync failed');
    } finally { setBusy(''); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-400 py-16 justify-center">
      <Loader2 className="animate-spin" size={16} /> Loading…
    </div>;
  }

  const live = status?.mode === 'live';
  // The badge reports what is stored; the dropdown reports what has been picked. When
  // they differ the screen contradicts itself, so say which is which rather than leaving
  // the reader to guess.
  const pendingMode = form.mode !== status?.mode;

  return (
    <div className="space-y-4">
      {/* Connection state */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className={`w-10 h-10 rounded-xl grid place-items-center ${
              status?.configured ? 'bg-emerald-50' : 'bg-slate-100'}`}>
              <CreditCard size={19} className={status?.configured ? 'text-emerald-600' : 'text-slate-400'} />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-bold text-slate-900">Stripe</p>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  live ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
                  {live ? 'Live mode' : 'Test mode'}
                </span>
                {pendingMode && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                    {form.mode === 'live' ? 'Live' : 'Test'} selected — not saved yet
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                {status?.configured
                  ? <>Secret key {status.secretKeyHint} · webhook {status.webhookConfigured ? 'configured' : 'NOT configured'}</>
                  : 'No keys configured — checkout is disabled.'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={test} disabled={busy || !status?.configured}
              title={status?.configured ? 'Check the stored key against Stripe' : 'Save an API key first'}
              className="px-3.5 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white">
              {busy === 'test' ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />} Test
            </button>
            <button onClick={sync} disabled={busy || !status?.configured}
              title={status?.configured ? 'Create the products and prices in Stripe' : 'Save an API key first'}
              className="px-3.5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-indigo-600">
              {busy === 'sync' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sync catalogue
            </button>
          </div>
        </div>

        {status?.modeMismatch && (
          <div className="mt-4 flex items-start gap-2 rounded-lg p-3 bg-rose-50 text-rose-800 text-sm">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>
              The stored key is a <b>{status.keyLooksLive ? 'live' : 'test'}</b> key but the mode is set to{' '}
              <b>{status.mode}</b>. Fix this before selling anything — one of these takes real money and the
              other does not.
            </span>
          </div>
        )}

        {testResult && (
          <div className={`mt-4 flex items-start gap-2 rounded-lg p-3 text-sm ${
            testResult.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>
            {testResult.ok ? <CheckCircle2 size={16} className="shrink-0 mt-0.5" /> : <AlertTriangle size={16} className="shrink-0 mt-0.5" />}
            <span>
              {testResult.ok
                ? <>Connected to <b>{testResult.businessName || testResult.account}</b> ({testResult.country?.toUpperCase()},{' '}
                    {testResult.defaultCurrency?.toUpperCase()}).{' '}
                    {testResult.chargesEnabled ? 'Charges are enabled.' : 'Charges are NOT yet enabled on this account.'}</>
                : testResult.message}
            </span>
          </div>
        )}
      </div>

      {/* Keys */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={16} className="text-slate-400" />
          <h3 className="font-bold text-slate-900">API keys</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Stored encrypted in the database, never in a file on the server. Leave a field blank to keep
          what is already stored. Keys are never shown again once saved.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Mode">
            <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="test">Test</option>
              <option value="live">Live</option>
            </select>
          </Field>
          <Field label="Publishable key" hint={status?.publishableKey || 'not set'}>
            <input value={form.publishableKey} onChange={e => setForm({ ...form, publishableKey: e.target.value })}
              placeholder={form.mode === 'live' ? 'pk_live_…' : 'pk_test_…'} autoComplete="off"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono" />
          </Field>
          <Field label="Secret key" hint={status?.secretKeyHint || 'not set'}>
            <input type="password" value={form.secretKey} onChange={e => setForm({ ...form, secretKey: e.target.value })}
              placeholder={form.mode === 'live' ? 'sk_live_…' : 'sk_test_…'} autoComplete="new-password"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono" />
          </Field>
          <Field label="Webhook signing secret" hint={status?.webhookSecretHint || 'not set'}>
            <input type="password" value={form.webhookSecret} onChange={e => setForm({ ...form, webhookSecret: e.target.value })}
              placeholder="whsec_…" autoComplete="new-password"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono" />
          </Field>
        </div>

        <div className="flex items-center justify-between gap-4 mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs text-slate-500">
            Add this endpoint in Stripe → Developers → Webhooks:
            <button onClick={() => { navigator.clipboard?.writeText(WEBHOOK_URL); toast.success('Copied'); }}
              className="ml-2 font-mono text-slate-700 hover:text-indigo-600 inline-flex items-center gap-1">
              {WEBHOOK_URL} <Copy size={11} />
            </button>
          </div>
          <button onClick={save} disabled={busy === 'save'}
            className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 inline-flex items-center gap-2">
            {busy === 'save' && <Loader2 size={14} className="animate-spin" />} Save keys
          </button>
        </div>
      </div>

      {/* Webhook deliveries */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-bold text-slate-900 mb-1">Recent webhook events</h3>
        <p className="text-sm text-slate-500 mb-3">
          The first place to look when a subscription looks wrong.
        </p>
        {events.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No events received yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="pb-2 font-semibold">Event</th>
                  <th className="pb-2 font-semibold">Type</th>
                  <th className="pb-2 font-semibold">Received</th>
                  <th className="pb-2 font-semibold">Result</th>
                </tr>
              </thead>
              <tbody>
                {events.map(ev => (
                  <tr key={ev.stripe_event_id} className="border-t border-slate-100">
                    <td className="py-2 font-mono text-xs text-slate-500">{ev.stripe_event_id}</td>
                    <td className="py-2 text-slate-700">{ev.type}</td>
                    <td className="py-2 text-slate-500">
                      {ev.created_at && new Date(ev.created_at).toLocaleString('en-AU')}
                    </td>
                    <td className="py-2">
                      {ev.error
                        ? <span className="text-rose-600 text-xs">{ev.error}</span>
                        : ev.processed_at
                          ? <span className="text-emerald-600 text-xs font-semibold">processed</span>
                          : <span className="text-amber-600 text-xs">pending</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-semibold text-slate-600">{label}</span>
        {hint && <span className="text-[11px] text-slate-400 font-mono">{hint}</span>}
      </span>
      {children}
    </label>
  );
}
